import React, { useEffect, useState } from 'react';
import { Country } from 'country-state-city';
import {
  getAllRegulations,
  getJurisdictions as getDashboardJurisdictions,
  type Jurisdiction as DashboardJurisdiction,
  type RegulationSummary,
} from '../../api/dashboardApi';
import { fetchEnterpriseProfile, type EnterpriseProfile } from '../../api/enterpriseApi';
import { useAuth } from '../../context/AuthContext';
import { ComparisonSection, DetailedPoint } from '../../api/comparisonApi';
import {
  CategoryPill,
  CategoryStatus,
  ComparisonStage,
  useComparisonSocket,
} from '../../hooks/useComparisonSocket';
import { Eyebrow, PageHeading } from '../../design-system';
import styles from './ComparisonPage.module.css';
import MatrixPanel from './MatrixPanel';
import { listDocuments as listMyDocuments, type GRCDocument as MyDocument } from '../../api/grcDocumentApi';

type FilterOption = 'all' | 'similar' | 'partial' | 'different' | 'standard' | 'policy_specific';

// Alignment colors map to design-system semantic tokens (success / warning / danger).
// Values reference --ds-* custom properties so they re-theme with the token set.
const ALIGNMENT_CONFIG = {
  similar: {
    label: 'Largely Similar',
    borderColor: 'var(--ds-success)',
    badgeBg: 'var(--ds-success-bg)',
    badgeText: 'var(--ds-success)',
  },
  partial: {
    label: 'Partially Aligned',
    borderColor: 'var(--ds-warning)',
    badgeBg: 'var(--ds-warning-bg)',
    badgeText: 'var(--ds-warning)',
  },
  different: {
    label: 'Significantly Different',
    borderColor: 'var(--ds-danger)',
    badgeBg: 'var(--ds-danger-bg)',
    badgeText: 'var(--ds-danger)',
  },
} as const;

// Neutral accent tokens for the two regulation columns (no raw hex in JSX).
const REG_ACCENT_1 = 'var(--ds-text-secondary)';
const REG_ACCENT_2 = 'var(--ds-text-muted)';

const STAGE_LABELS: Record<ComparisonStage, string> = {
  idle: '',
  fetching: 'Fetching regulation content...',
  extracting: 'Extracting comparable points...',
  comparing: 'Comparing categories...',
  summarizing: 'Generating executive summary...',
  done: 'Done',
  error: 'Error',
};

const SCOPE_LABELS: Record<JurisdictionScope, string> = {
  primary: 'Primary',
  active: 'Active',
  upcoming: 'Upcoming',
  operating: 'Operating',
  available: 'All jurisdictions',
};

const ALL_COUNTRIES = Country.getAllCountries();
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  usa: 'United States of America',
  us: 'United States of America',
  uk: 'United Kingdom',
  uae: 'United Arab Emirates',
};

type JurisdictionScope = 'primary' | 'active' | 'upcoming' | 'operating' | 'available';

type CompanyJurisdictionOption = {
  value: string;
  label: string;
  scope: JurisdictionScope;
};

function normalizeJurisdiction(value: string | null | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/[–—,-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatJurisdictionDisplay(value: string | null | undefined): string {
  const raw = value?.trim();
  if (!raw) return '';

  const parts = raw
    .split(/\s*[–—-]\s*|\s*,\s*/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return raw;

  const uniqueParts = parts.filter((part, index) => (
    parts.findIndex((candidate) => normalizeJurisdiction(candidate) === normalizeJurisdiction(part)) === index
  ));

  return uniqueParts.join(', ');
}

function pushJurisdictionOption(
  options: CompanyJurisdictionOption[],
  seen: Set<string>,
  value: string | null | undefined,
  scope: JurisdictionScope,
) {
  const cleanValue = formatJurisdictionDisplay(value);
  if (!cleanValue) return;

  const key = normalizeJurisdiction(cleanValue);
  if (!key || seen.has(key)) return;

  const prefix = scope === 'primary'
    ? 'Primary'
    : scope === 'active'
      ? 'Active'
      : scope === 'upcoming'
        ? 'Upcoming'
        : scope === 'available'
          ? 'All jurisdictions'
          : 'Operating';

  seen.add(key);
  options.push({ value: cleanValue, label: `${prefix} · ${cleanValue}`, scope });
}

function buildJurisdictionOptions(
  enterpriseProfile: EnterpriseProfile | null,
  availableJurisdictions: DashboardJurisdiction[] = [],
): CompanyJurisdictionOption[] {
  const options: CompanyJurisdictionOption[] = [];
  const seen = new Set<string>();

  // The company's own jurisdictions come first — they're the common case, so
  // they stay at the top of the dropdown as convenient defaults.
  pushJurisdictionOption(options, seen, enterpriseProfile?.jurisdiction, 'primary');
  enterpriseProfile?.other_jurisdictions?.forEach((value) => pushJurisdictionOption(options, seen, value, 'active'));
  enterpriseProfile?.new_jurisdictions?.forEach((value) => pushJurisdictionOption(options, seen, value, 'upcoming'));

  // …then EVERY jurisdiction that has regulations in the platform, so users can
  // compare any two — not just the ones registered at signup. Sorted by name;
  // duplicates of the company's own entries are skipped by `seen`.
  [...availableJurisdictions]
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .forEach((jurisdiction) => {
      pushJurisdictionOption(options, seen, jurisdiction.name, 'available');
      // Also offer the parent country (e.g. "India" for "Kerala") so
      // country-level comparisons are reachable.
      if (jurisdiction.country_name) {
        pushJurisdictionOption(options, seen, jurisdiction.country_name, 'available');
      }
    });

  return options;
}

function isKnownCountry(value: string | null | undefined): boolean {
  const normalized = normalizeJurisdiction(value);
  if (!normalized) return false;
  if (COUNTRY_NAME_ALIASES[normalized]) return true;

  return ALL_COUNTRIES.some((country) => (
    normalizeJurisdiction(country.name) === normalized
    || normalizeJurisdiction(country.isoCode) === normalized
  ));
}

function matchesDashboardJurisdiction(jurisdiction: DashboardJurisdiction, selectedJurisdiction: string): boolean {
  const target = normalizeJurisdiction(selectedJurisdiction);
  const name = normalizeJurisdiction(jurisdiction.name);
  const country = normalizeJurisdiction(jurisdiction.country_name);
  const parts = formatJurisdictionDisplay(selectedJurisdiction)
    .split(/\s*,\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  const nonCountryParts = parts
    .filter((part) => !isKnownCountry(part))
    .map((part) => normalizeJurisdiction(part));
  const combined = normalizeJurisdiction([jurisdiction.name, jurisdiction.country_name].filter(Boolean).join(' '));
  const reversedCombined = normalizeJurisdiction([jurisdiction.country_name, jurisdiction.name].filter(Boolean).join(' '));

  if (nonCountryParts.length > 0) {
    return nonCountryParts.some((part) => (
      name === part
      || combined === part
      || reversedCombined === part
      || name.startsWith(`${part} `)
      || part.startsWith(`${name} `)
    ));
  }

  if (name === target || country === target || combined === target || reversedCombined === target) return true;
  if (name.startsWith(`${target} `) || country.startsWith(`${target} `)) return true;
  if (target.startsWith(`${name} `) || target.startsWith(`${country} `)) return true;

  return false;
}

function getJurisdictionCodeCandidates(jurisdictions: DashboardJurisdiction[]): string[] {
  return Array.from(
    new Set(
      jurisdictions
        .map((jurisdiction) => jurisdiction.code?.trim())
        .filter((code): code is string => !!code),
    ),
  );
}

async function searchJurisdictionCandidates(selectedJurisdiction: string): Promise<DashboardJurisdiction[]> {
  const formatted = formatJurisdictionDisplay(selectedJurisdiction);
  const parts = formatted
    .split(/\s*,\s*/)
    .map((part) => part.trim())
    .filter(Boolean);

  const searchTerms = Array.from(new Set([
    formatted,
    ...parts,
    ...parts.filter((part) => !isKnownCountry(part)),
    parts[0] || '',
    parts.length > 1 ? parts[parts.length - 1] : '',
  ].filter(Boolean)));

  const results = await Promise.all(
    searchTerms.map((term) => getDashboardJurisdictions({ q: term, limit: 100 })),
  );

  const merged = new Map<string, DashboardJurisdiction>();
  results.flat().forEach((jurisdiction) => {
    merged.set(jurisdiction.id, jurisdiction);
  });

  return Array.from(merged.values());
}

function formatArtifactOptionLabel(artifact: RegulationSummary): string {
  const title = (artifact.title || 'Untitled regulation').trim();
  return title.length > 84 ? `${title.slice(0, 81)}…` : title;
}

// ─── Point Card ───────────────────────────────────────────────────────────────

const PointCard: React.FC<{ point: DetailedPoint; accentColor: string }> = ({
  point,
  accentColor,
}) => (
  <div className={styles.pointCard} style={{ borderLeftColor: accentColor }}>
    <p className={styles.pointText}>{point.point}</p>
    <div className={styles.pointMeta}>
      {point.explanation && (
        <div className={styles.pointMetaBlock}>
          <span className={styles.pointMetaLabel}>What it means</span>
          <span className={styles.pointMetaValue}>{point.explanation}</span>
        </div>
      )}
      {point.impact && (
        <div className={styles.pointMetaBlock}>
          <span className={styles.pointMetaLabel} data-type="impact">Impact</span>
          <span className={styles.pointMetaValue}>{point.impact}</span>
        </div>
      )}
    </div>
  </div>
);

// ─── Comparison Bar ───────────────────────────────────────────────────────────

const ComparisonBar: React.FC<{
  section: ComparisonSection;
  reg1Title: string;
  reg2Title: string;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ section, reg1Title, reg2Title, isExpanded, onToggle }) => {
  const cfg = ALIGNMENT_CONFIG[section.alignment];

  return (
    <div className={styles.bar}>
      <button
        className={styles.barHeader}
        onClick={onToggle}
        style={{ borderLeftColor: cfg.borderColor }}
      >
        <div className={styles.barLeft}>
          <div className={styles.barTitleRow}>
            <span className={styles.barCategory}>{section.category}</span>
            {!section.is_standard && (
              <span className={styles.dynamicTag}>Policy-specific</span>
            )}
          </div>
          {section.key_insight && (
            <span className={styles.barInsight}>{section.key_insight}</span>
          )}
        </div>
        <div className={styles.barRight}>
          <span
            className={styles.alignmentBadge}
            style={{ background: cfg.badgeBg, color: cfg.badgeText }}
          >
            {section.alignment_label}
          </span>
          <span className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}>
            ›
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className={styles.barBody}>
          {section.section_summary && (
            <div className={styles.sectionSummary}>
              <span className={styles.sectionSummaryLabel}>Overview</span>
              <p className={styles.sectionSummaryText}>{section.section_summary}</p>
            </div>
          )}

          {section.comparison_impact && (
            <div
              className={styles.comparisonImpact}
              style={{ borderColor: cfg.borderColor, background: cfg.badgeBg }}
            >
              <span className={styles.comparisonImpactLabel} style={{ color: cfg.badgeText }}>
                What this means in practice
              </span>
              <p className={styles.comparisonImpactText}>{section.comparison_impact}</p>
            </div>
          )}

          <div className={styles.barColumns}>
            <div className={styles.barColumn}>
              <div className={styles.barColumnHeader} style={{ borderBottomColor: REG_ACCENT_1 }}>
                {reg1Title}
              </div>
              {section.regulation_1_summary && (
                <p className={styles.barColumnSummary}>{section.regulation_1_summary}</p>
              )}
              {section.regulation_1_points.length > 0 && (
                <div className={styles.pointsList}>
                  {section.regulation_1_points.map((pt, i) => (
                    <PointCard key={i} point={pt} accentColor={REG_ACCENT_1} />
                  ))}
                </div>
              )}
            </div>

            <div className={styles.barColumnDivider} style={{ background: cfg.borderColor }} />

            <div className={styles.barColumn}>
              <div className={styles.barColumnHeader} style={{ borderBottomColor: REG_ACCENT_2 }}>
                {reg2Title}
              </div>
              {section.regulation_2_summary && (
                <p className={styles.barColumnSummary}>{section.regulation_2_summary}</p>
              )}
              {section.regulation_2_points.length > 0 && (
                <div className={styles.pointsList}>
                  {section.regulation_2_points.map((pt, i) => (
                    <PointCard key={i} point={pt} accentColor={REG_ACCENT_2} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Special Bar (Unique / Common Ground) ────────────────────────────────────

const SpecialBar: React.FC<{ title: string; items: string[]; accentColor: string }> = ({
  title,
  items,
  accentColor,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.bar}>
      <button
        className={styles.barHeader}
        onClick={() => setOpen(o => !o)}
        style={{ borderLeftColor: accentColor }}
      >
        <div className={styles.barLeft}>
          <span className={styles.barCategory}>{title}</span>
          <span className={styles.barInsight}>
            {items.length} provision{items.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className={styles.barRight}>
          <span className={`${styles.expandIcon} ${open ? styles.expandIconOpen : ''}`}>›</span>
        </div>
      </button>
      {open && (
        <div className={styles.barBody}>
          <ul className={styles.specialList}>
            {items.map((item, i) => (
              <li key={i} className={styles.specialListItem}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Progress Panel (thinking-style) ─────────────────────────────────────────

const STATUS_ICON: Record<CategoryStatus, string> = {
  pending: '○',
  comparing: '◌',
  done: '✓',
};

const ProgressPanel: React.FC<{
  stage: ComparisonStage;
  stageMessage: string;
  categories: CategoryPill[];
  completedCount: number;
  total: number;
}> = ({ stage, stageMessage, categories, completedCount, total }) => {
  const isPreCategory = stage === 'fetching' || stage === 'extracting';

  return (
    <div className={styles.progressPanel}>
      <div className={styles.progressHeader}>
        <div className={styles.progressSpinner} />
        <span className={styles.progressStageText}>
          {stageMessage || STAGE_LABELS[stage]}
        </span>
      </div>

      {!isPreCategory && categories.length > 0 && (
        <>
          <div className={styles.progressMeta}>
            {completedCount}/{total} categories compared
          </div>
          <div className={styles.categoryGrid}>
            {categories.map(cat => (
              <div
                key={cat.category_id}
                className={styles.categoryPill}
                data-status={cat.status}
                title={cat.category}
              >
                <span className={styles.categoryPillIcon}>{STATUS_ICON[cat.status]}</span>
                <span className={styles.categoryPillName}>{cat.category}</span>
                {!cat.is_standard && (
                  <span className={styles.categoryPillDynamic}>•</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const ComparisonPage: React.FC = () => {
  const { token } = useAuth();
  const { state, compare, reset } = useComparisonSocket();

  const [artifactId1, setArtifactId1] = useState(() => localStorage.getItem('comparison_artifactId1') ?? '');
  const [artifactId2, setArtifactId2] = useState(() => localStorage.getItem('comparison_artifactId2') ?? '');
  const [matchedArtifacts1, setMatchedArtifacts1] = useState<RegulationSummary[]>([]);
  const [matchedArtifacts2, setMatchedArtifacts2] = useState<RegulationSummary[]>([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<CompanyJurisdictionOption[]>([]);
  const [selectedJurisdiction1, setSelectedJurisdiction1] = useState(
    () => localStorage.getItem('comparison_jurisdiction1') ?? localStorage.getItem('comparison_jurisdiction') ?? '',
  );
  const [selectedJurisdiction2, setSelectedJurisdiction2] = useState(
    () => localStorage.getItem('comparison_jurisdiction2') ?? '',
  );
  const [setupLoading, setSetupLoading] = useState(true);
  const [artifactLoading1, setArtifactLoading1] = useState(false);
  const [artifactLoading2, setArtifactLoading2] = useState(false);
  const [fallbackNote1, setFallbackNote1] = useState<string | null>(null);
  const [fallbackNote2, setFallbackNote2] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterOption>('all');
  const [mode, setMode] = useState<'pair' | 'matrix'>('pair');
  const [myDocuments, setMyDocuments] = useState<MyDocument[]>([]);
  useEffect(() => { listMyDocuments().then(setMyDocuments).catch(() => setMyDocuments([])); }, []);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setSetupLoading(true);
      try {
        // Load the company profile AND the full jurisdiction catalogue so any
        // two jurisdictions can be compared (not only the registered ones).
        const [enterpriseProfile, availableJurisdictions] = await Promise.all([
          fetchEnterpriseProfile().catch(() => null),
          getDashboardJurisdictions({ limit: 500 }).catch(() => [] as DashboardJurisdiction[]),
        ]);

        if (cancelled) return;

        const options = buildJurisdictionOptions(enterpriseProfile, availableJurisdictions);
        const fallbackJurisdiction = options[0]?.value
          || formatJurisdictionDisplay(enterpriseProfile?.jurisdiction)
          || formatJurisdictionDisplay(enterpriseProfile?.other_jurisdictions?.[0])
          || formatJurisdictionDisplay(enterpriseProfile?.new_jurisdictions?.[0]);

        if (!options.length && fallbackJurisdiction) {
          options.push({
            value: fallbackJurisdiction,
            label: `Primary · ${fallbackJurisdiction}`,
            scope: 'primary',
          });
        }

        setJurisdictionOptions(options);
        setSelectedJurisdiction1((prev) => {
          const normalizedPrev = formatJurisdictionDisplay(prev);
          const hasPrevInOptions = !!normalizedPrev && options.some((option) => (
            normalizeJurisdiction(option.value) === normalizeJurisdiction(normalizedPrev)
          ));
          const value = hasPrevInOptions ? normalizedPrev : (fallbackJurisdiction || '');
          if (value) localStorage.setItem('comparison_jurisdiction1', value);
          else localStorage.removeItem('comparison_jurisdiction1');
          return value;
        });
        // jurisdiction2 keeps whatever the user last chose (or empty for cross-jurisdiction pick)
        setSelectedJurisdiction2((prev) => {
          const normalizedPrev = formatJurisdictionDisplay(prev);
          const hasPrevInOptions = !!normalizedPrev && options.some((option) => (
            normalizeJurisdiction(option.value) === normalizeJurisdiction(normalizedPrev)
          ));
          return hasPrevInOptions ? normalizedPrev : prev;
        });

        if (!options.length && !fallbackJurisdiction) {
          setInputError('No jurisdictions available to compare yet.');
        }
      } catch {
        if (!cancelled) {
          setInputError('Could not load jurisdictions.');
        }
      } finally {
        if (!cancelled) setSetupLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (setupLoading) return;
    if (!selectedJurisdiction1) {
      setMatchedArtifacts1([]);
      setArtifactId1('');
      setFallbackNote1(null);
      return;
    }
    (async () => {
      setArtifactLoading1(true);
      setFallbackNote1(null);
      try {
        const jurisdictionResults = await searchJurisdictionCandidates(selectedJurisdiction1);
        if (cancelled) return;
        const matchingJurisdictions = jurisdictionResults.filter((j) => matchesDashboardJurisdiction(j, selectedJurisdiction1));
        const usedFallback = matchingJurisdictions.length === 0 && jurisdictionResults.length > 0;
        const candidates = usedFallback ? jurisdictionResults : matchingJurisdictions;
        if (usedFallback) {
          const fallbackName = jurisdictionResults[0]?.country_name ?? 'a broader region';
          setFallbackNote1(`No regulations found specifically for "${selectedJurisdiction1}" — showing regulations from ${fallbackName} instead.`);
        }
        const jurisdictionCodes = getJurisdictionCodeCandidates(candidates);
        if (!jurisdictionCodes.length) {
          setMatchedArtifacts1([]);
          setArtifactId1('');
          setInputError(`No regulation artifacts found for ${selectedJurisdiction1}.`);
          return;
        }
        const responses = await Promise.all(
          jurisdictionCodes.slice(0, 5).map((code) => getAllRegulations({ jurisdiction_code: code, limit: 200, sort_by: 'date', sort_order: 'desc' })),
        );
        if (cancelled) return;
        const uniqueArtifacts = new Map<string, RegulationSummary>();
        responses.forEach((r) => r.regulations.forEach((reg) => uniqueArtifacts.set(String(reg.id), reg)));
        const artifacts = Array.from(uniqueArtifacts.values());
        setMatchedArtifacts1(artifacts);
        setArtifactId1((prev) => {
          const value = artifacts.some((a) => String(a.id) === prev) ? prev : String(artifacts[0]?.id ?? '');
          if (value) localStorage.setItem('comparison_artifactId1', value);
          return value;
        });
        setInputError(artifacts.length ? null : `No regulation artifacts found for ${selectedJurisdiction1}.`);
      } catch {
        if (!cancelled) {
          setMatchedArtifacts1([]);
          setArtifactId1('');
          setInputError('Could not load regulation artifacts for Jurisdiction 1.');
        }
      } finally {
        if (!cancelled) setArtifactLoading1(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedJurisdiction1, setupLoading]);

  useEffect(() => {
    let cancelled = false;
    if (setupLoading) return;
    if (!selectedJurisdiction2) {
      setMatchedArtifacts2([]);
      setArtifactId2('');
      setFallbackNote2(null);
      return;
    }
    (async () => {
      setArtifactLoading2(true);
      setFallbackNote2(null);
      try {
        const jurisdictionResults = await searchJurisdictionCandidates(selectedJurisdiction2);
        if (cancelled) return;
        const matchingJurisdictions = jurisdictionResults.filter((j) => matchesDashboardJurisdiction(j, selectedJurisdiction2));
        const usedFallback = matchingJurisdictions.length === 0 && jurisdictionResults.length > 0;
        const candidates = usedFallback ? jurisdictionResults : matchingJurisdictions;
        if (usedFallback) {
          const fallbackName = jurisdictionResults[0]?.country_name ?? 'a broader region';
          setFallbackNote2(`No regulations found specifically for "${selectedJurisdiction2}" — showing regulations from ${fallbackName} instead.`);
        }
        const jurisdictionCodes = getJurisdictionCodeCandidates(candidates);
        if (!jurisdictionCodes.length) {
          setMatchedArtifacts2([]);
          setArtifactId2('');
          setInputError(`No regulation artifacts found for ${selectedJurisdiction2}.`);
          return;
        }
        const responses = await Promise.all(
          jurisdictionCodes.slice(0, 5).map((code) => getAllRegulations({ jurisdiction_code: code, limit: 200, sort_by: 'date', sort_order: 'desc' })),
        );
        if (cancelled) return;
        const uniqueArtifacts = new Map<string, RegulationSummary>();
        responses.forEach((r) => r.regulations.forEach((reg) => uniqueArtifacts.set(String(reg.id), reg)));
        const artifacts = Array.from(uniqueArtifacts.values());
        setMatchedArtifacts2(artifacts);
        setArtifactId2((prev) => {
          const value = artifacts.some((a) => String(a.id) === prev) ? prev : String(artifacts[0]?.id ?? '');
          if (value) localStorage.setItem('comparison_artifactId2', value);
          return value;
        });
        setInputError(artifacts.length ? null : `No regulation artifacts found for ${selectedJurisdiction2}.`);
      } catch {
        if (!cancelled) {
          setMatchedArtifacts2([]);
          setArtifactId2('');
          setInputError('Could not load regulation artifacts for Jurisdiction 2.');
        }
      } finally {
        if (!cancelled) setArtifactLoading2(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedJurisdiction2, setupLoading]);

  const isActive = state.stage !== 'idle' && state.stage !== 'done' && state.stage !== 'error';

  const handleCompare = () => {
    const isDoc = (v: string) => v.startsWith('document:');
    if ((!selectedJurisdiction1 && !isDoc(artifactId1)) || (!selectedJurisdiction2 && !isDoc(artifactId2))) {
      setInputError('Select a jurisdiction for both regulations first, or pick one of your documents.');
      return;
    }
    if (!artifactId1.trim() || !artifactId2.trim()) {
      setInputError('Please select both regulation artifacts.');
      return;
    }
    if (artifactId1.trim() === artifactId2.trim()) {
      setInputError('Please select two different artifact IDs.');
      return;
    }
    setInputError(null);
    setFilter('all');
    setExpandedSections(new Set());
    compare([artifactId1.trim(), artifactId2.trim()], token!);
  };

  const handleReset = () => {
    reset();
    setFilter('all');
    setExpandedSections(new Set());
    setInputError(null);
  };

  const toggleSection = (categoryId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  // Result data
  const meta = state.meta;
  const summary = state.summary;
  const sections = state.sections;
  const reg1Title = meta?.regulation_1.title ?? meta?.regulation_1.artifact_id ?? 'Regulation 1';
  const reg2Title = meta?.regulation_2.title ?? meta?.regulation_2.artifact_id ?? 'Regulation 2';
  const score = summary?.overall_similarity_score ?? 0;
  const scoreColor = score >= 70 ? 'var(--ds-success)' : score >= 40 ? 'var(--ds-warning)' : 'var(--ds-danger)';

  const completedCount = state.categories.filter(c => c.status === 'done').length;
  const standardCount = sections.filter(s => s.is_standard).length;
  const dynamicCount = sections.filter(s => !s.is_standard).length;
  const alignmentCounts = {
    similar: sections.filter(s => s.alignment === 'similar').length,
    partial: sections.filter(s => s.alignment === 'partial').length,
    different: sections.filter(s => s.alignment === 'different').length,
  };

  const filteredSections = sections.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'standard') return s.is_standard;
    if (filter === 'policy_specific') return !s.is_standard;
    return s.alignment === filter;
  });

  const selectedOption1 = jurisdictionOptions.find((option) => option.value === selectedJurisdiction1);
  const selectedOption2 = jurisdictionOptions.find((option) => option.value === selectedJurisdiction2);
  const selectedArtifact1 = matchedArtifacts1.find((artifact) => String(artifact.id) === artifactId1);
  const selectedArtifact2 = matchedArtifacts2.find((artifact) => String(artifact.id) === artifactId2);

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div style={{ display: 'flex', gap: 6, marginBottom: '1rem' }} role="tablist" aria-label="Comparison mode">
          {(['pair', 'matrix'] as const).map((m) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)}
                    className={styles.compareBtn} style={{ opacity: mode === m ? 1 : 0.55, padding: '0.45rem 0.9rem' }}>
              {m === 'pair' ? 'Two-way comparison' : 'Requirement matrix (2–5 sides)'}
            </button>
          ))}
        </div>
        {mode === 'matrix' && token && <MatrixPanel token={token} />}
        {/* Input Section */}
        <div className={styles.inputSection} hidden={mode === 'matrix'}>
          {/* Banner */}
          <div className={styles.formBanner}>
            <div className={styles.formBannerInner}>
              <div className={styles.formBannerCopy}>
                <Eyebrow tone="default" dot>REGULATION COMPARISON</Eyebrow>
                <PageHeading
                  accentWord="side by side"
                  accentTone="orange"
                  size="h2"
                  align="left"
                  level="h2"
                  className={styles.formTitle}
                >
                  Compare two regulations side by side
                </PageHeading>
                <p className={styles.formSubtitle}>Select a jurisdiction and two regulation artifacts to generate an AI-powered comparison report.</p>
              </div>
              <div className={styles.formBannerSteps}>
                <div className={styles.formStep}>
                  <span className={styles.formStepNum}>1</span>
                  <span className={styles.formStepText}>Pick jurisdiction</span>
                </div>
                <div className={styles.formStep}>
                  <span className={styles.formStepNum}>2</span>
                  <span className={styles.formStepText}>Select regulations</span>
                </div>
                <div className={styles.formStep}>
                  <span className={styles.formStepNum}>3</span>
                  <span className={styles.formStepText}>Generate report</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.formBody}>

          <div className={styles.inputRow}>
            {/* ── Side 1 ── */}
            <div className={styles.inputGroup}>
              <label htmlFor="comparison-jurisdiction-1" className={styles.inputLabel}>Jurisdiction 1</label>
              <select
                id="comparison-jurisdiction-1"
                className={styles.input}
                value={selectedJurisdiction1}
                onChange={(e) => {
                  setSelectedJurisdiction1(e.target.value);
                  localStorage.setItem('comparison_jurisdiction1', e.target.value);
                  reset();
                  setFilter('all');
                  setExpandedSections(new Set());
                  setInputError(null);
                }}
                disabled={setupLoading || isActive}
              >
                <option value="">
                  {setupLoading ? 'Loading jurisdictions...' : 'Select a jurisdiction'}
                </option>
                {jurisdictionOptions.map((option) => (
                  <option key={`j1-${option.scope}-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedJurisdiction1 && selectedOption1 && (
                <span className={`${styles.scopeBadge} ${styles[`scope_${selectedOption1.scope}`]}`} style={{ marginTop: 4, display: 'inline-block' }}>
                  {SCOPE_LABELS[selectedOption1.scope]}
                </span>
              )}

              <label htmlFor="comparison-artifact-1" className={styles.inputLabel} style={{ marginTop: 12 }}>Regulation 1</label>
              <select
                id="comparison-artifact-1"
                className={styles.input}
                value={artifactId1}
                onChange={(e) => {
                  setArtifactId1(e.target.value);
                  localStorage.setItem('comparison_artifactId1', e.target.value);
                  setInputError(null);
                }}
                disabled={!selectedJurisdiction1 || artifactLoading1 || isActive || matchedArtifacts1.length === 0}
              >
                <option value="">
                  {!selectedJurisdiction1
                    ? 'Select a jurisdiction first'
                    : artifactLoading1
                      ? 'Loading artifacts...'
                      : matchedArtifacts1.length
                        ? 'Select first regulation'
                        : 'No artifacts available'}
                </option>
                {matchedArtifacts1.map((artifact) => (
                  <option key={`artifact-1-${artifact.id}`} value={String(artifact.id)}>
                    {formatArtifactOptionLabel(artifact)}
                  </option>
                ))}
              </select>
              <span className={styles.inputHint}>
                {selectedArtifact1
                  ? `Artifact ID: ${selectedArtifact1.id} · ${selectedArtifact1.jurisdiction_name}`
                  : 'Choose the first regulation to compare'}
              </span>
              {myDocuments.length > 0 && (
                <label className={styles.inputHint} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                  …or use one of your documents
                  <select className={styles.input} value={artifactId1.startsWith('document:') ? artifactId1 : ''} onChange={(e) => { setArtifactId1(e.target.value); setInputError(null); }} disabled={isActive}>
                    <option value="">Choose a document</option>
                    {myDocuments.map((d) => <option key={d.id} value={`document:${d.id}`}>{d.document_name}</option>)}
                  </select>
                </label>
              )}
              {fallbackNote1 && (
                <div className={styles.fallbackNote}>
                  <strong>Note:</strong> {fallbackNote1} The listed regulations are from the closest available region — you can still proceed with these.
                </div>
              )}
            </div>

            <div className={styles.inputVs}>VS</div>

            {/* ── Side 2 ── */}
            <div className={styles.inputGroup}>
              <label htmlFor="comparison-jurisdiction-2" className={styles.inputLabel}>Jurisdiction 2</label>
              <select
                id="comparison-jurisdiction-2"
                className={styles.input}
                value={selectedJurisdiction2}
                onChange={(e) => {
                  setSelectedJurisdiction2(e.target.value);
                  localStorage.setItem('comparison_jurisdiction2', e.target.value);
                  reset();
                  setFilter('all');
                  setExpandedSections(new Set());
                  setInputError(null);
                }}
                disabled={setupLoading || isActive}
              >
                <option value="">
                  {setupLoading ? 'Loading jurisdictions...' : 'Select a jurisdiction'}
                </option>
                {jurisdictionOptions.map((option) => (
                  <option key={`j2-${option.scope}-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedJurisdiction2 && selectedOption2 && (
                <span className={`${styles.scopeBadge} ${styles[`scope_${selectedOption2.scope}`]}`} style={{ marginTop: 4, display: 'inline-block' }}>
                  {SCOPE_LABELS[selectedOption2.scope]}
                </span>
              )}

              <label htmlFor="comparison-artifact-2" className={styles.inputLabel} style={{ marginTop: 12 }}>Regulation 2</label>
              <select
                id="comparison-artifact-2"
                className={styles.input}
                value={artifactId2}
                onChange={(e) => {
                  setArtifactId2(e.target.value);
                  localStorage.setItem('comparison_artifactId2', e.target.value);
                  setInputError(null);
                }}
                disabled={!selectedJurisdiction2 || artifactLoading2 || isActive || matchedArtifacts2.length === 0}
              >
                <option value="">
                  {!selectedJurisdiction2
                    ? 'Select a jurisdiction first'
                    : artifactLoading2
                      ? 'Loading artifacts...'
                      : matchedArtifacts2.length
                        ? 'Select second regulation'
                        : 'No artifacts available'}
                </option>
                {matchedArtifacts2.map((artifact) => (
                  <option key={`artifact-2-${artifact.id}`} value={String(artifact.id)}>
                    {formatArtifactOptionLabel(artifact)}
                  </option>
                ))}
              </select>
              <span className={styles.inputHint}>
                {selectedArtifact2
                  ? `Artifact ID: ${selectedArtifact2.id} · ${selectedArtifact2.jurisdiction_name}`
                  : 'Choose the second regulation for side-by-side review'}
              </span>
              {myDocuments.length > 0 && (
                <label className={styles.inputHint} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                  …or use one of your documents
                  <select className={styles.input} value={artifactId2.startsWith('document:') ? artifactId2 : ''} onChange={(e) => { setArtifactId2(e.target.value); setInputError(null); }} disabled={isActive}>
                    <option value="">Choose a document</option>
                    {myDocuments.map((d) => <option key={d.id} value={`document:${d.id}`}>{d.document_name}</option>)}
                  </select>
                </label>
              )}
              {fallbackNote2 && (
                <div className={styles.fallbackNote}>
                  <strong>Note:</strong> {fallbackNote2} The listed regulations are from the closest available region — you can still proceed with these.
                </div>
              )}
            </div>
          </div>

          {(inputError || state.error) && (
            <p className={styles.errorMsg}>{inputError ?? state.error}</p>
          )}

          <div className={styles.compareBtnRow}>
            {isActive ? (
              <button className={`${styles.compareBtn} ${styles.compareBtnCancel}`} onClick={handleReset}>
                Cancel Run
              </button>
            ) : state.stage === 'done' || state.stage === 'error' ? (
              <button className={styles.compareBtn} onClick={handleReset}>
                Start New Comparison
              </button>
            ) : (
              <button
                className={styles.compareBtn}
                onClick={handleCompare}
                disabled={!artifactId1.trim() || !artifactId2.trim() || artifactLoading1 || artifactLoading2}
              >
                Generate Comparison
              </button>
            )}
          </div>

          </div>{/* /formBody */}
      </div>{/* /inputSection */}

      {/* Feature hint cards — visible only when idle */}
      {state.stage === 'idle' && (
        <div className={styles.featureCards}>
          <div className={styles.featureCard}>
            <div className={styles.featureCardIcon} data-variant="info">
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className={styles.featureCardBody}>
              <span className={styles.featureCardTitle}>Category-by-category breakdown</span>
              <p className={styles.featureCardText}>Each regulation is compared across standard and policy-specific dimensions with an alignment rating per category.</p>
            </div>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureCardIcon} data-variant="success">
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className={styles.featureCardBody}>
              <span className={styles.featureCardTitle}>Alignment score & executive summary</span>
              <p className={styles.featureCardText}>Get a 0–100 alignment score plus an executive summary of key similarities, differences, and practical implications.</p>
            </div>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureCardIcon} data-variant="info">
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className={styles.featureCardBody}>
              <span className={styles.featureCardTitle}>Unique provisions & common ground</span>
              <p className={styles.featureCardText}>Highlights what's unique to each regulation and what both share, so you can quickly identify gaps and overlaps.</p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Panel — shows during active comparison */}
      {isActive && (
        <ProgressPanel
          stage={state.stage}
          stageMessage={state.stageMessage}
          categories={state.categories}
          completedCount={completedCount}
          total={state.totalCategories}
        />
      )}

      {/* Summarizing banner (after all categories done, waiting for summary) */}
      {state.stage === 'summarizing' && (
        <div className={styles.summarizingBanner}>
          <div className={styles.progressSpinner} />
          <span>{state.stageMessage}</span>
        </div>
      )}

      {/* Progressive Results — appear as sections complete */}
      {sections.length > 0 && (
        <div className={styles.results}>

          {/* Summary strip — shows once summary event arrives */}
          {summary && meta && (
            <div className={styles.summaryStrip}>
              <div className={styles.scoreBlock} style={{ borderColor: scoreColor }}>
                <span className={styles.scoreNum} style={{ color: scoreColor }}>{score}</span>
                <span className={styles.scoreDenom}>/100</span>
                <span className={styles.scoreCaption}>Alignment Score</span>
              </div>
              <div className={styles.summaryText}>
                <div className={styles.regVsRow}>
                  <span className={styles.regVsTitle}>{reg1Title}</span>
                  <span className={styles.regVsSep}>vs</span>
                  <span className={styles.regVsTitle}>{reg2Title}</span>
                </div>
                <p className={styles.execSummary}>{summary.executive_summary}</p>
                <div className={styles.summaryStats}>
                  <span className={styles.statPill}>{sections.length} dimensions</span>
                  {dynamicCount > 0 && (
                    <span className={styles.statPill} data-type="dynamic">
                      {dynamicCount} policy-specific
                    </span>
                  )}
                  <span className={styles.statPill} data-type="similar">{alignmentCounts.similar} similar</span>
                  <span className={styles.statPill} data-type="partial">{alignmentCounts.partial} partial</span>
                  <span className={styles.statPill} data-type="different">{alignmentCounts.different} different</span>
                </div>
              </div>
            </div>
          )}

          {/* Regulation metadata */}
          {meta && (
            <div className={styles.metaRow}>
              {[meta.regulation_1, meta.regulation_2].map((reg, i) => (
                <div key={i} className={styles.metaCard} data-index={i}>
                  <div className={styles.metaRegLabel}>Regulation {i + 1}</div>
                  <div className={styles.metaTitle}>{reg.title ?? '—'}</div>
                  <div className={styles.metaDetails}>
                    {reg.jurisdiction && <span>{reg.jurisdiction}</span>}
                    {reg.status && <span className={styles.metaStatus}>{reg.status}</span>}
                    {reg.effective_date && <span>Effective: {String(reg.effective_date).split('T')[0]}</span>}
                    {reg.fetched_at && <span title="When this regulation's content was last fetched into our database">Data as of: {String(reg.fetched_at).split('T')[0]}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filter bar — only show once we have some sections */}
          {sections.length > 0 && (
            <div className={styles.filterBar}>
              <span className={styles.filterLabel}>Filter:</span>
              <div className={styles.filterPills}>
                <button
                  className={`${styles.filterPill} ${filter === 'all' ? styles.filterPillActive : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All <span className={styles.pillCount}>{sections.length}</span>
                </button>
                {(['similar', 'partial', 'different'] as const).map(a => (
                  <button
                    key={a}
                    className={`${styles.filterPill} ${filter === a ? styles.filterPillActive : ''}`}
                    onClick={() => setFilter(a)}
                    data-alignment={a}
                  >
                    {ALIGNMENT_CONFIG[a].label}
                    <span className={styles.pillCount}>{alignmentCounts[a]}</span>
                  </button>
                ))}
                {standardCount > 0 && (
                  <button
                    className={`${styles.filterPill} ${filter === 'standard' ? styles.filterPillActive : ''}`}
                    onClick={() => setFilter('standard')}
                  >
                    Standard <span className={styles.pillCount}>{standardCount}</span>
                  </button>
                )}
                {dynamicCount > 0 && (
                  <button
                    className={`${styles.filterPill} ${filter === 'policy_specific' ? styles.filterPillActive : ''}`}
                    onClick={() => setFilter('policy_specific')}
                    data-alignment="dynamic"
                  >
                    Policy-specific <span className={styles.pillCount}>{dynamicCount}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Comparison bars */}
          <div className={styles.barsContainer}>
            {filteredSections.length === 0 ? (
              <div className={styles.emptyState}>No sections match this filter.</div>
            ) : (
              filteredSections.map(section => (
                <ComparisonBar
                  key={section.category_id}
                  section={section}
                  reg1Title={reg1Title}
                  reg2Title={reg2Title}
                  isExpanded={expandedSections.has(section.category_id)}
                  onToggle={() => toggleSection(section.category_id)}
                />
              ))
            )}
          </div>

          {/* Unique + Common Ground — only after done */}
          {state.stage === 'done' && summary && (
            summary.unique_to_regulation_1.length > 0 ||
            summary.unique_to_regulation_2.length > 0 ||
            summary.common_grounds.length > 0
          ) ? (
            <div className={styles.specialBarsSection}>
              <h3 className={styles.specialBarsHeading}>Additional Insights</h3>
              <div className={styles.barsContainer}>
                {summary!.unique_to_regulation_1.length > 0 && (
                  <SpecialBar
                    title={`Unique to ${reg1Title}`}
                    items={summary!.unique_to_regulation_1}
                    accentColor={REG_ACCENT_1}
                  />
                )}
                {summary!.unique_to_regulation_2.length > 0 && (
                  <SpecialBar
                    title={`Unique to ${reg2Title}`}
                    items={summary!.unique_to_regulation_2}
                    accentColor={REG_ACCENT_2}
                  />
                )}
                {summary!.common_grounds.length > 0 && (
                  <SpecialBar
                    title="Common Ground"
                    items={summary!.common_grounds}
                    accentColor="var(--ds-success)"
                  />
                )}
              </div>
            </div>
          ) : null}

        </div>
      )}
      </div>
    </div>
  );
};
